/**
 * Helper: dataLayer
 * -----------------------------------------------------------------------------
 * Handle Data Layer action events.
 *
 */

/**
 * Send add to cart event.
 * @param {Object} variantData - Variant added to cart
 * @param {Object} productData - Product added to cart.
 */
export function addToCartEvent(variantData, productData) {
  if (!productData || !variantData) {
    return
  }

  track({
    event: 'custom_add_to_cart',
    currencyCode: window.cnvs.store.currency,
    products: [formatProductData(variantData, productData)],

    // predefined ga4 dimensions
    ...formatEcommerceEvent({ variant: variantData, product: productData }),
  })
}

/**
 * Format product details into UDL accepted product.
 * @param {Object} variant - Variant added to cart
 * @param {Object} product - Product data.
 * @returns {Object}
 */
export function formatProductData(variant, product) {
  return {
    item_name: product.title,
    productID: product.id,
    price: (variant.price.amount || variant.price) / 100,
    brand: 'John Hardy',
    category: product.type,
    variantID: variant.id,
    sku: variant.sku,
    quantity: 1,
    currency: variant.price.currencyCode || window.cnvs.store.currency,
  }
}

/**
 * Format line item response to be match Google Analytics ecommerce measurements
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_or_remove_an_item_from_a_shopping_cart
 * @param product - Product added to cart
 * @param variant - Variant added to cart
 * @returns {Object}
 */
export function formatEcommerceEvent({ variant, product }) {
  return {
    currency: window.cnvs.store.currency,
    value: (variant || product).price.amount / 100,
    items: [formatEcommerceItem({ product, variant })],
  }
}

/**
 * Format an item for Google Analytics add to cart event
 * @param product - Product added to cart
 * @param variant - Variant added to cart
 * @returns {Object}
 */
export function formatEcommerceItem({ product, variant }) {
  let style = product.tags.find((tag) => tag.includes('style_no:'))

  if (style) {
    style = style.replace('style_no:', '').trim()
  }

  let id = style || `shopify_US_${product.id}`

  if (!style && variant) {
    id += `_${variant.id}`
  }

  return {
    item_brand: product.vendor,
    item_id: id,
    item_name: product.title,
    item_variant: variant?.title || '',
    item_sku: variant?.sku || '',
    item_category: product.type,
    price: (variant || product).price.amount / 100,
    quantity: 1,
  }
}

/**
 * Format line item response to be match Google Analytics ecommerce
 * measurements. Ensure that a product is attached to each variant.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_or_remove_an_item_from_a_shopping_cart
 * @param {array} variants - Variants added to cart
 * @returns {Object}
 */
export function formatVariantsEcommerceEvent(variants) {
  const items = variants.map((variant) => {
    return formatEcommerceItem({ variant, product: variant.product })
  })
  const total = items.reduce((price, currentItem) => {
    return price + currentItem.price
  }, 0)

  return {
    currency: window.cnvs.store.currency,
    value: total,
    items,
  }
}

/**
 * Login in account event.
 * @params {String} payload.email - Customer email
 * @params {Number} payload.id - Customer ID
 */
export function loginEvent({ email, id }) {
  trackGtm({
    event: 'login_success',
    email,
    customer_id: id ? id.toString() : '',
  })
}

/**
 * Push data to both GTM and configured gtag
 * @param {String | Object} data - Data or event name to push to data layer
 */
export function track(data = {}) {
  window.TrackAnalytics(data, { gtm: true, gtag: true })
}

/**
 * Push data to gtag only
 * @param {String | Object} data - Data or event name to push to data layer.
 * If using data as an object, `event` is a required attribute.
 */
export function trackGtag(data) {
  window.TrackAnalytics(data, { gtm: false, gtag: true })
}

/**
 * Push data to GTM only
 * @param {String | Object} data - Data or event name to push to data layer
 */
export function trackGtm(data) {
  window.TrackAnalytics(data, { gtm: true, gtag: false })
}

/**
 * Track wishlist navigation clicks. Wishlist link refers to the link to the wishlist page.
 * @param {String} source - Where the wishlist link was clicked
 */
export function trackWishlistLink(source) {
  track({
    event: 'wishlist_navigation',
    page_location: window.location.href,
    jh_source: source,
  })
}

/**
 * Track site header and menu drawer clicks
 * @param {string} eventName - The event name to track
 * @param {object} clickEvent - Click event from click listener
 * @param {object} parameters - Additional parameters to attach to the analytic event
 */
export function trackNavigationClick(eventName, clickEvent, parameters = {}) {
  const href = clickEvent?.currentTarget?.getAttribute('href')
  const label = clickEvent?.currentTarget?.textContent?.trim() ?? ''
  if (!href || !eventName) {
    return
  }
  track({
    event: eventName,
    label: label, // deprecated
    url: href, // deprecated
    link_text: label,
    link_url: href,
    ...parameters,
  })
}
